<template>
  <el-dialog
    title="Edit Environment"
    :visible.sync="show"
    @close="$emit('update:isVisible', false)"
    :close-on-click-modal="false"
  >
    <el-form @keyup.enter.native="handleSubmit">
      <el-radio-group
        v-model="type"
        style="margin-bottom: 10px"
        @change="resetModal(false)"
      >
        <!-- <el-radio label="local">Planning Analytics Local</el-radio> -->
        <el-radio label="agent">Planning Analytics Local</el-radio>
        <el-radio label="SaaS">Planning Analytics as a Service</el-radio>
        <el-radio label="cloud">Planning Analytics Cloud</el-radio>
      </el-radio-group>
      <el-form-item v-if="type === 'agent'">
        <span slot="label">
          Agent
          <small>
            <el-tooltip
              class="modal-info"
              placement="right"
              effect="light"
              content="The agent DataMaestro should use to connect to this Environment.  If you or Revelwood did not set up an agent, select local without agent."
            >
              <i
                class="el-icon-info"
                style="position: relative; left: 2px; bottom: 4px"
              />
            </el-tooltip>
          </small>
        </span>
        <el-select v-model="selectedAgent" placeholder="Select an agent">
          <el-option
            v-for="item in options"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          >
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item>
        <span slot="label">
          Friendly Name
          <small>
            <el-tooltip
              class="modal-info"
              placement="right"
              effect="light"
              content="Used to identify the Planning Analytics environment throughout DataMaestro"
            >
              <i
                class="el-icon-info"
                style="position: relative; left: 2px; bottom: 4px"
              />
            </el-tooltip>
          </small>
        </span>
        <el-input
          v-model="$v.name.$model"
          :class="{ error: checkValid && $v.name.$invalid }"
        />
      </el-form-item>
      <el-form-item>
        <span slot="label">
          {{
            type === "SaaS"
              ? "Service URL"
              : type === "cloud"
              ? "IBM Environment Name"
              : "Admin Host"
          }}
          <small>
            <el-tooltip
              class="modal-info"
              placement="right"
              effect="light"
              :content="`${
                type === 'SaaS'
                  ? 'Your Planning Analytics as a Service URL (e.g. us-east-1.planninganalytics.saas.ibm.com)'
                  : type === 'cloud'
                  ? 'The Planning Analytics for Microsoft Excel system URL provided in the Welcome Kit'
                  : 'IP address or fully qualified domain name (FQDN) of the Planning Analytics host server'
              }`"
            >
              <i
                class="el-icon-info"
                style="position: relative; left: 2px; bottom: 4px"
              />
            </el-tooltip>
          </small>
        </span>
        <el-input
          v-model="$v.adminHost.$model"
          :class="{ error: checkValid && $v.adminHost.$invalid }"
          ><span v-if="['cloud', 'SaaS'].includes(type)" slot="prepend"
            >https://</span
          >
          <span v-if="type === 'cloud'" slot="append"
            >.planning-analytics.ibmcloud.com</span
          >
        </el-input>
      </el-form-item>
      <el-form-item v-if="type === 'cloud'">
        <span slot="label">
          Non-interactive Account
          <small>
            <el-tooltip
              class="modal-info"
              placement="right"
              effect="light"
              content="Reach out to Revelwood or IBM if missing from the Welcome Kit"
            >
              <i
                class="el-icon-info"
                style="position: relative; left: 2px; bottom: 4px"
              />
            </el-tooltip>
          </small>
        </span>
        <el-input
          placeholder="Username"
          v-model="$v.username.$model"
          :class="{ error: checkValid && $v.username.$invalid }"
        />
        <el-input
          style="margin-top: 10px"
          placeholder="Password"
          type="password"
          show-password
          v-model="$v.password.$model"
          :class="{ error: checkValid && $v.password.$invalid }"
        />
      </el-form-item>
      <el-form-item v-if="type === 'SaaS'">
        <span slot="label">
          Tenant ID:
          <small>
            <el-tooltip
              class="modal-info"
              placement="right"
              effect="light"
              content="When in the PA SaaS interface, check the url for '&tenantId=' to find your ID"
            >
              <i
                class="el-icon-info"
                style="position: relative; left: 2px; bottom: 4px"
              />
            </el-tooltip>
          </small>
        </span>
        <el-input
          placeholder="Tenant ID"
          v-model="$v.tenantId.$model"
          :class="{ error: checkValid && $v.tenantId.$invalid }"
        />
      </el-form-item>
      <el-form-item v-if="type === 'SaaS'">
        <span slot="label">
          API Key:
          <small>
            <el-tooltip
              class="modal-info"
              placement="right"
              effect="light"
              content="If you don't have an API Key, follow the instructions at the link below"
            >
              <i
                class="el-icon-info"
                style="position: relative; left: 2px; bottom: 4px"
              />
            </el-tooltip>
          </small>
        </span>
        <el-input
          placeholder="API Key"
          type="password"
          show-password
          v-model="$v.apiKey.$model"
          :class="{ error: checkValid && $v.apiKey.$invalid }"
        />
      </el-form-item>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <span>
        <small v-if="type === 'SaaS'">
          <a
            href="https://cloud.ibm.com/docs/planning-analytics?topic=planning-analytics-api__key_based_authentication"
            target="_blank"
          >
            Need to create an API Key?
          </a>
        </small>
      </span>
      <el-button class="button" @click="handleSubmit">Save</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { required, requiredIf } from "vuelidate/lib/validators";
import EnvironmentService from "../../services/EnvironmentService";
import AgentService from "../../services/AgentService";
import { mapState } from "vuex";
import Helper from "../../utilities/Helper";

/**
 * @vue-data {String} [name=""] - New name of the environment
 * @vue-data {String} [adminHost=""] - New adminhost of the environment
 * @vue-data {Boolean} [checkValid=false] - Flag that indicates whether to check for errors
 */
export default {
  props: {
    envSelected: Object,
    isVisible: Boolean,
  },
  data() {
    return {
      name: null,
      adminHost: null,
      checkValid: false,
      show: this.isVisible,
      type: "agent",
      username: null,
      password: null,
      options: [],
      selectedAgent: null,
      apiKey: null,
      tenantId: null,
    };
  },
  computed: {
    ...mapState(["organizationId", "instanceId"]),
  },
  watch: {
    isVisible(value) {
      if (value) this.resetModal(true);
      this.show = value;
    },
  },
  methods: {
    handleError: Helper.handleError,
    trim: Helper.trim,
    /**
     * Resets the modal
     * @function
     */
    resetModal(isVisible) {
      if (isVisible) {
        this.name = this.envSelected.name;
        this.type = this.envSelected.isCloud === "Yes" ? "cloud" : "agent";
        this.adminHost = this.envSelected.adminHost;
        this.username = this.envSelected.username;
        this.selectedAgent = this.envSelected.agentId;
        if (this.envSelected.isSaaS) this.type = "SaaS";
        this.apiKey = this.envSelected.apiKey;
        this.tenantId = this.envSelected.tenantId;
      } else {
        this.adminHost = null;
        this.username = null;
        this.selectedAgent = null;
      }
      this.password = null;
      this.checkValid = false;
    },
    /**
     * Submits the new environment information
     * @async
     * @function
     */
    async handleSubmit() {
      this.checkValid = true;
      if (
        this.$v.name.$invalid ||
        this.$v.adminHost.$invalid ||
        this.$v.username.$invalid ||
        this.$v.password.$invalid ||
        this.$v.apiKey.$invalid ||
        this.$v.tenantId.$$invalid
      ) {
        return;
      }

      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        await EnvironmentService.update({
          organizationId: this.organizationId,
          name: this.envSelected.name,
          adminHost: this.envSelected.adminHost,
          newName: this.trim(this.name),
          newAdminHost: this.trim(this.adminHost),
          isCloud: this.type === "cloud",
          isSaaS: this.type === "SaaS",
          cloudUsername: this.trim(this.username),
          cloudPassword: this.trim(this.password),
          agentId: this.selectedAgent,
          tenantId: this.trim(this.tenantId),
          apiKey: this.trim(this.apiKey),
        });
        this.$message({
          type: "success",
          message: `"${this.name}" environment updated successfully`,
          duration: 5000,
          showClose: true,
        });
        this.checkValid = false;
        this.$emit("update:isVisible", false);
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    async getAgentOptions() {
      try {
        const response = await AgentService.getAll({
          organizationId: this.organizationId,
        });
        this.options = response.data.agents.map((agent) => {
          return {
            value: agent.id,
            label: agent.name,
          };
        });
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      }
    },
  },
  validations() {
    return {
      name: { required },
      adminHost: { required },
      username: {
        required: requiredIf(() => {
          return this.type === "cloud";
        }),
      },
      password: {
        required: requiredIf(() => {
          return this.type === "cloud";
        }),
      },
      apiKey: {
        required: requiredIf(() => {
          return this.type === "SaaS";
        }),
      },
      tenantId: {
        required: requiredIf(() => {
          return this.type === "SaaS";
        }),
      },
    };
  },
  mounted() {
    this.getAgentOptions();
  },
};
</script>

<style scoped>
.dialog-footer {
  display: flex;
  justify-content: space-between;
}
.error :deep(.el-input__inner) {
  border: 1px solid red;
  border-radius: 4px;
}
.button {
  background-color: #b3282d;
  color: white;
  border-color: white;
}
.button:focus {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.button:hover {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.cancel-button {
  background-color: #7a7c80;
  color: white;
  border-color: white;
}
.cancel-button:focus {
  background-color: #606266;
  color: white;
  border-color: white;
  outline: none;
}
.cancel-button:hover {
  background-color: #606266;
  color: white;
  border-color: white;
  outline: none;
}
</style>
